import React, { useEffect, useState, useCallback } from 'react';
import { SpaceBetween, Box, Button, Spinner } from '@cloudscape-design/components';
import { browse } from './browseClient';
import { imgBase } from '../config';
import Scoreboard from './Scoreboard';
import AgentRevealBanner from './AgentRevealBanner';

// The Continental editorial palette.
const INK = '#1a1714';
const PAPER = '#f7f3ea';
const CLARET = '#7c2128';
const HAIR = '#e2dac9';

function Masthead({ name, tagline }) {
  return (
    <div style={{ textAlign: 'center', padding: '6px 0 10px', borderBottom: `2px solid ${INK}` }}>
      <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 34, fontWeight: 800, color: INK, letterSpacing: '-0.5px' }}>
        {name || 'The Continental'}
      </div>
      <div style={{ fontStyle: 'italic', color: '#7a7064', fontSize: 13 }}>{tagline || 'Independent journalism since 1924'}</div>
    </div>
  );
}

function Article({ id, onBack }) {
  const [a, setA] = useState(null);
  useEffect(() => {
    let alive = true;
    browse.publisher({ article: id }).then((d) => alive && setA(d)).catch(() => alive && setA({ error: true }));
    return () => { alive = false; };
  }, [id]);
  if (!a) return <Spinner />;
  if (a.error) return <Box color="text-status-error">Article not found.</Box>;
  return (
    <div style={{ background: PAPER, padding: 16, borderRadius: 10 }}>
      <Button iconName="angle-left" variant="link" onClick={onBack}>Back to The Continental</Button>
      {a.section && <div style={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: 11, color: CLARET, fontWeight: 700, marginTop: 8 }}>{a.section}</div>}
      <div style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 800, color: INK, lineHeight: 1.15, margin: '4px 0' }}>{a.title}</div>
      {a.dek && <div style={{ fontStyle: 'italic', color: '#4a443d', fontSize: 15 }}>{a.dek}</div>}
      <img
        src={`${imgBase()}/img/publisher/${id}.jpg`}
        alt=""
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
        style={{ width: '100%', maxHeight: 280, objectFit: 'cover', borderRadius: 6, border: `1px solid ${HAIR}`, margin: '10px 0' }}
      />
      <div style={{ fontSize: 12, color: '#8a7f70', margin: '8px 0', borderTop: `1px solid ${HAIR}`, borderBottom: `1px solid ${HAIR}`, padding: '6px 0' }}>
        {a.byline ? `By ${a.byline} · ` : ''}{a.date}
      </div>
      <div style={{ color: INK, lineHeight: 1.6 }}>{a.body || a.text || a.dek}</div>
    </div>
  );
}

const SECTION_BTN = (active) => ({
  border: 'none', background: 'none', cursor: 'pointer',
  fontFamily: 'Georgia, serif', fontSize: 14, padding: '4px 2px',
  color: active ? CLARET : INK, fontWeight: active ? 700 : 500,
  borderBottom: active ? `2px solid ${CLARET}` : '2px solid transparent',
});

export default function ContinentalSite({ reveals = [] }) {
  const [data, setData] = useState(null);
  const [section, setSection] = useState(null); // null=All, 'scores'=scores, else slug
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((sec) => {
    setLoading(true);
    browse.publisher(sec ? { section: sec } : {})
      .then(setData)
      .catch(() => setData({ articles: [], sections: [] }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(null); }, [load]);

  if (openId) return <Article id={openId} onBack={() => setOpenId(null)} />;

  return (
    <div style={{ background: PAPER, padding: 16, borderRadius: 10 }}>
      <Masthead name={data?.name} tagline={data?.tagline} />

      {reveals.length > 0 && (
        <div style={{ margin: '10px 0' }}>
          <AgentRevealBanner reveals={reveals} accent={CLARET} />
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', padding: '8px 0', borderBottom: `1px solid ${HAIR}`, marginBottom: 12 }}>
        <button style={SECTION_BTN(!section)} onClick={() => { setSection(null); load(null); }}>Front Page</button>
        {(data?.sections || []).map((s) => (
          <button key={s.slug} style={SECTION_BTN(section === s.slug)} onClick={() => { setSection(s.slug); load(s.slug); }}>
            {s.name}
          </button>
        ))}
        <button style={SECTION_BTN(section === 'scores')} onClick={() => setSection('scores')}>⚽ Scores</button>
      </div>

      {section === 'scores' ? (
        <Scoreboard />
      ) : loading ? (
        <Spinner />
      ) : (
        <SpaceBetween size="s">
          {(data?.articles || []).map((a, i) => (
            <div key={a.id} style={{ borderBottom: `1px solid ${HAIR}`, paddingBottom: 10, display: 'flex', gap: 12 }}>
              <img
                src={`${imgBase()}/img/publisher/${a.id}.jpg`}
                alt=""
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                style={{ width: i === 0 ? 160 : 96, height: i === 0 ? 100 : 64, objectFit: 'cover', borderRadius: 4, border: `1px solid ${HAIR}`, flex: 'none' }}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  {a.section && <span style={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: 10, color: CLARET, fontWeight: 700 }}>{a.section}</span>}
                  <span style={{ fontSize: 11, color: '#a99e8c' }}>{a.date}</span>
                </div>
                <button onClick={() => setOpenId(a.id)} style={{ border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}>
                  <div style={{ fontFamily: 'Georgia, serif', fontSize: i === 0 ? 22 : 17, fontWeight: 700, color: INK, lineHeight: 1.2 }}>{a.title}</div>
                </button>
                {a.dek && <div style={{ fontSize: 13, color: '#4a443d', marginTop: 2 }}>{a.dek}</div>}
              </div>
            </div>
          ))}
          {(data?.articles || []).length === 0 && (
            <Box textAlign="center" color="text-body-secondary">No articles.</Box>
          )}
        </SpaceBetween>
      )}
    </div>
  );
}
